/**
 * /projects/:projectId/gmp — GMP (Guaranteed Maximum Price) contract dashboard.
 *
 * Tabs:
 *   Dashboard     — KPI tiles: Original/Current GMP, contingency %, projected savings
 *   Contingency   — draw history, manual draw form, reverse action
 *   Allowances    — contract allowances with actual_amount edit + reconcile
 *   Open Book     — monthly open-book cost report by category
 *   Closeout      — gate-check status + savings preview + confirm button
 *
 * Pack-gated: only shown when entity.gmp_enabled = true.
 * If no gmp_contracts row exists for the project, shows the contract setup wizard.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  gmpContractByProjectQueryOptions,
  gmpContingencyQueryOptions,
  gmpAllowancesQueryOptions,
  gmpOpenBookQueryOptions,
  gmpSavingsQueryOptions,
  gmpKeys,
  myEntitiesQueryOptions,
} from "@/lib/queries";
import { api } from "@/lib/api";
import { fmtMoney } from "@/components/FinancialStatementSection";
import type {
  GMPContract,
  GMPContractCreate,
  GMPAllowance,
  GMPContingencyDraw,
  OpenBookReport,
  SavingsCalc,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/projects/$projectId/gmp")({
  component: GmpPage,
});

// ── Formatting helpers ─────────────────────────────────────────────────────────

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function fmtNum(n: number | string): string {
  return fmtMoney(Number(n));
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 7); // YYYY-MM
}

// ── Contract setup form ────────────────────────────────────────────────────────

function ContractSetupForm({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    original_gmp: "",
    contingency_original: "",
    savings_split_owner_pct: "0.5",
    savings_split_contractor_pct: "0.5",
    savings_split_cap: "",
    open_book_required: true,
    audit_rights_enabled: true,
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: GMPContractCreate) =>
      api<GMPContract>("/api/money/gmp", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gmpKeys.byProject(projectId) });
    },
    onError: (err: Error) => setError(err.message),
  });

  function handleOwnerPct(v: string) {
    const n = parseFloat(v) || 0;
    setForm(f => ({
      ...f,
      savings_split_owner_pct: v,
      savings_split_contractor_pct: String(Math.round((1 - n / 100) * 10000) / 10000 * 100 / 100),
    }));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const ownerPct = parseFloat(form.savings_split_owner_pct);
    mutation.mutate({
      project_id: projectId,
      original_gmp: parseFloat(form.original_gmp),
      contingency_original: parseFloat(form.contingency_original),
      savings_split_owner_pct: ownerPct / 100,
      savings_split_contractor_pct: 1 - ownerPct / 100,
      savings_split_cap: form.savings_split_cap ? parseFloat(form.savings_split_cap) : null,
      open_book_required: form.open_book_required,
      audit_rights_enabled: form.audit_rights_enabled,
    });
  }

  return (
    <div className="max-w-xl mx-auto px-6 py-12">
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-1">
        Set up GMP Contract
      </h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        Define the financial terms for this Guaranteed Maximum Price contract.
        You can add allowances and cost-code classifications after setup.
      </p>

      {error && (
        <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Original GMP ($)
          </label>
          <input
            required
            type="number"
            step="0.01"
            min="0"
            value={form.original_gmp}
            onChange={e => setForm(f => ({ ...f, original_gmp: e.target.value }))}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
            placeholder="e.g. 11500000"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Contingency Amount ($)
          </label>
          <input
            required
            type="number"
            step="0.01"
            min="0"
            value={form.contingency_original}
            onChange={e => setForm(f => ({ ...f, contingency_original: e.target.value }))}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
            placeholder="e.g. 500000"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Owner savings split (%)
            </label>
            <input
              type="number"
              step="1"
              min="0"
              max="100"
              value={form.savings_split_owner_pct}
              onChange={e => handleOwnerPct(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Contractor savings split (%)
            </label>
            <input
              type="number"
              readOnly
              value={100 - parseFloat(form.savings_split_owner_pct || "0")}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-slate-50 dark:border-gray-700 dark:bg-gray-800 cursor-not-allowed"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Contractor savings cap ($) <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.savings_split_cap}
            onChange={e => setForm(f => ({ ...f, savings_split_cap: e.target.value }))}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
            placeholder="Leave blank for no cap"
          />
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={form.open_book_required}
              onChange={e => setForm(f => ({ ...f, open_book_required: e.target.checked }))}
            />
            Open-book reporting required
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={form.audit_rights_enabled}
              onChange={e => setForm(f => ({ ...f, audit_rights_enabled: e.target.checked }))}
            />
            Audit rights (full sub detail)
          </label>
        </div>

        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md px-4 py-2 disabled:opacity-50"
        >
          {mutation.isPending ? "Creating…" : "Create GMP Contract"}
        </button>
      </form>
    </div>
  );
}

// ── KPI tile ───────────────────────────────────────────────────────────────────

function KpiTile({
  label, value, sub, highlight,
}: { label: string; value: string; sub?: string; highlight?: "green" | "amber" | "red" }) {
  const color = highlight === "green" ? "text-emerald-700" : highlight === "amber" ? "text-amber-600" : highlight === "red" ? "text-red-700" : "text-slate-900";
  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">{label}</div>
      <div className={`text-xl font-bold font-mono ${color} dark:text-slate-100`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Dashboard tab ──────────────────────────────────────────────────────────────

function DashboardTab({ contract }: { contract: GMPContract }) {
  const savingsQ = useQuery(gmpSavingsQueryOptions(contract.id));
  const savings = savingsQ.data;

  const pctRem = contract.contingency_remaining_pct;
  const contHighlight: "green" | "amber" | "red" = pctRem > 0.5 ? "green" : pctRem > 0.25 ? "amber" : "red";

  return (
    <div className="space-y-6">
      {/* Status badge */}
      {contract.status !== "active" && (
        <div className={`text-sm px-4 py-2 rounded-lg border font-medium ${
          contract.status === "closed"
            ? "bg-slate-100 border-slate-300 text-slate-600"
            : "bg-amber-50 border-amber-300 text-amber-700"
        }`}>
          Contract status: <strong>{contract.status === "closing_out" ? "Closing Out" : "Closed"}</strong>
          {contract.closed_out_at && (
            <span className="font-normal ml-2 text-xs">
              · Closed {contract.closed_out_at.slice(0, 10)}
            </span>
          )}
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile label="Original GMP" value={fmtNum(contract.original_gmp)} />
        <KpiTile label="Current GMP" value={fmtNum(contract.current_gmp)} />
        <KpiTile
          label="Contingency Remaining"
          value={fmtNum(contract.contingency_remaining)}
          sub={`${pct(pctRem)} of original`}
          highlight={contHighlight}
        />
        {savings ? (
          <KpiTile
            label="Projected Savings"
            value={fmtNum(savings.savings)}
            sub={`Owner: ${fmtNum(savings.owner_share)} · GC: ${fmtNum(savings.contractor_share)}`}
            highlight="green"
          />
        ) : (
          <KpiTile label="Projected Savings" value="—" sub="loading…" />
        )}
      </div>

      {/* Contract terms */}
      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 uppercase tracking-wide">
          Contract Terms
        </h3>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-500">Savings split</dt>
            <dd className="font-mono text-slate-800 dark:text-slate-200">
              {pct(Number(contract.savings_split_owner_pct))} owner /&nbsp;
              {pct(Number(contract.savings_split_contractor_pct))} GC
            </dd>
          </div>
          {contract.savings_split_cap && (
            <div className="flex justify-between">
              <dt className="text-slate-500">GC savings cap</dt>
              <dd className="font-mono text-slate-800 dark:text-slate-200">{fmtNum(contract.savings_split_cap)}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-slate-500">Open-book reporting</dt>
            <dd className="text-slate-800 dark:text-slate-200">{contract.open_book_required ? "Required" : "Optional"}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Audit rights</dt>
            <dd className="text-slate-800 dark:text-slate-200">{contract.audit_rights_enabled ? "Full detail" : "Bid-tabbed only"}</dd>
          </div>
        </dl>
        {contract.status === "closed" && contract.final_savings !== null && (
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-gray-800">
            <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Final Closeout</h4>
            <dl className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <dt className="text-slate-500 text-xs">Final Savings</dt>
                <dd className="font-mono font-semibold text-emerald-700">{fmtNum(contract.final_savings!)}</dd>
              </div>
              <div>
                <dt className="text-slate-500 text-xs">Owner Share</dt>
                <dd className="font-mono font-semibold">{fmtNum(contract.final_owner_share!)}</dd>
              </div>
              <div>
                <dt className="text-slate-500 text-xs">GC Share</dt>
                <dd className="font-mono font-semibold text-emerald-700">{fmtNum(contract.final_contractor_share!)}</dd>
              </div>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Contingency tab ────────────────────────────────────────────────────────────

function ContingencyTab({ contract }: { contract: GMPContract }) {
  const qc = useQueryClient();
  const contQ = useQuery(gmpContingencyQueryOptions(contract.id));
  const [showDraw, setShowDraw] = useState(false);
  const [drawForm, setDrawForm] = useState({ amount: "", reason: "" });
  const [drawError, setDrawError] = useState<string | null>(null);

  const drawMutation = useMutation({
    mutationFn: (payload: { amount: number; reason: string }) =>
      api(`/api/money/gmp/${contract.id}/contingency-draws`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gmpKeys.contingency(contract.id) });
      qc.invalidateQueries({ queryKey: gmpKeys.byProject(contract.project_id) });
      setShowDraw(false);
      setDrawForm({ amount: "", reason: "" });
    },
    onError: (err: Error) => setDrawError(err.message),
  });

  const reverseMutation = useMutation({
    mutationFn: ({ drawId, reason }: { drawId: string; reason: string }) =>
      api(`/api/money/gmp/${contract.id}/contingency-draws/${drawId}/reverse`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gmpKeys.contingency(contract.id) });
      qc.invalidateQueries({ queryKey: gmpKeys.byProject(contract.project_id) });
    },
  });

  const status = contQ.data;
  const isActive = contract.status === "active";

  return (
    <div className="space-y-4">
      {/* Contingency meter */}
      {status && (
        <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-5">
          <div className="flex items-baseline gap-4 mb-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-0.5">Remaining</div>
              <div className="text-2xl font-bold font-mono text-slate-900 dark:text-slate-100">
                {fmtNum(status.contingency_remaining)}
              </div>
            </div>
            <div className="text-slate-400">/</div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-0.5">Original</div>
              <div className="text-lg font-mono text-slate-600 dark:text-slate-300">{fmtNum(status.contingency_original)}</div>
            </div>
          </div>
          <div className="w-full bg-slate-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                status.contingency_remaining_pct > 0.5 ? "bg-emerald-500" :
                status.contingency_remaining_pct > 0.25 ? "bg-amber-500" : "bg-red-500"
              }`}
              style={{ width: `${Math.min(status.contingency_remaining_pct * 100, 100)}%` }}
            />
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {pct(status.contingency_remaining_pct)} remaining · {fmtNum(status.total_drawn)} drawn
          </div>
          {status.contingency_remaining_pct <= 0.25 && (
            <div className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              ⚠ Contingency below 25% — further draws may require a formal Change Order.
            </div>
          )}
        </div>
      )}

      {/* Manual draw form */}
      {isActive && (
        <div>
          {!showDraw ? (
            <button
              onClick={() => setShowDraw(true)}
              className="text-sm border border-indigo-300 text-indigo-700 hover:bg-indigo-50 rounded-md px-3 py-1.5 dark:text-indigo-400 dark:border-indigo-600"
            >
              + Manual draw
            </button>
          ) : (
            <div className="bg-white dark:bg-gray-900 border border-indigo-200 dark:border-indigo-700 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Manual contingency draw</h4>
              {drawError && <p className="text-xs text-red-600 mb-2">{drawError}</p>}
              <div className="flex gap-3">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Amount ($)"
                  value={drawForm.amount}
                  onChange={e => setDrawForm(f => ({ ...f, amount: e.target.value }))}
                  className="border border-slate-300 rounded-md px-3 py-1.5 text-sm w-36 dark:border-gray-700 dark:bg-gray-900"
                />
                <input
                  type="text"
                  placeholder="Reason"
                  value={drawForm.reason}
                  onChange={e => setDrawForm(f => ({ ...f, reason: e.target.value }))}
                  className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900"
                />
                <button
                  onClick={() => drawMutation.mutate({ amount: parseFloat(drawForm.amount), reason: drawForm.reason })}
                  disabled={drawMutation.isPending || !drawForm.amount || !drawForm.reason}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md px-4 py-1.5 disabled:opacity-50"
                >
                  {drawMutation.isPending ? "Drawing…" : "Draw"}
                </button>
                <button onClick={() => setShowDraw(false)} className="text-slate-500 hover:text-slate-700 text-sm px-2">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Draws table */}
      {status && status.active_draws.length > 0 && (
        <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Date</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Reason</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Source</th>
                <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Amount</th>
                {isActive && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
              {status.active_draws.map((draw) => (
                <tr key={draw.id} className="hover:bg-slate-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{draw.approved_at.slice(0, 10)}</td>
                  <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{draw.reason}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      draw.source === "pco_auto"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-slate-100 text-slate-600"
                    }`}>
                      {draw.source === "pco_auto" ? "PCO Auto" : "Manual"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900 dark:text-slate-100">
                    {fmtNum(draw.amount)}
                  </td>
                  {isActive && (
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => {
                          const reason = window.prompt("Reason for reversal:");
                          if (reason) reverseMutation.mutate({ drawId: draw.id, reason });
                        }}
                        className="text-xs text-slate-400 hover:text-red-600"
                      >
                        Reverse
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {status && status.reversed_draws.length > 0 && (
        <details className="text-sm text-slate-500">
          <summary className="cursor-pointer">
            {status.reversed_draws.length} reversed draw(s)
          </summary>
          <div className="mt-2 pl-4 space-y-1">
            {status.reversed_draws.map(d => (
              <div key={d.id} className="text-xs text-slate-400 line-through">
                {d.approved_at.slice(0, 10)} · {d.reason} · {fmtNum(d.amount)}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ── Allowances tab ─────────────────────────────────────────────────────────────

function AllowancesTab({ contract }: { contract: GMPContract }) {
  const qc = useQueryClient();
  const allowQ = useQuery(gmpAllowancesQueryOptions(contract.id));
  const [addForm, setAddForm] = useState({ description: "", allowance_amount: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editActual, setEditActual] = useState("");

  const addMutation = useMutation({
    mutationFn: (payload: { description: string; allowance_amount: number }) =>
      api(`/api/money/gmp/${contract.id}/allowances`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gmpKeys.allowances(contract.id) });
      setShowAdd(false);
      setAddForm({ description: "", allowance_amount: "" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, actual_amount }: { id: string; actual_amount: number }) =>
      api(`/api/money/gmp/${contract.id}/allowances/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ actual_amount }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gmpKeys.allowances(contract.id) });
      setEditingId(null);
    },
  });

  const reconcileMutation = useMutation({
    mutationFn: (id: string) =>
      api(`/api/money/gmp/${contract.id}/allowances/${id}/reconcile`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gmpKeys.allowances(contract.id) });
    },
  });

  const allowances = allowQ.data ?? [];
  const isActive = contract.status === "active";

  return (
    <div className="space-y-4">
      {isActive && (
        <div>
          {!showAdd ? (
            <button
              onClick={() => setShowAdd(true)}
              className="text-sm border border-indigo-300 text-indigo-700 hover:bg-indigo-50 rounded-md px-3 py-1.5 dark:text-indigo-400"
            >
              + Add allowance
            </button>
          ) : (
            <div className="bg-white dark:bg-gray-900 border border-indigo-200 dark:border-indigo-700 rounded-lg p-4">
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Description (e.g. Lobby finishes)"
                  value={addForm.description}
                  onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))}
                  className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900"
                />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Amount ($)"
                  value={addForm.allowance_amount}
                  onChange={e => setAddForm(f => ({ ...f, allowance_amount: e.target.value }))}
                  className="w-36 border border-slate-300 rounded-md px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900"
                />
                <button
                  onClick={() => addMutation.mutate({ description: addForm.description, allowance_amount: parseFloat(addForm.allowance_amount) })}
                  disabled={addMutation.isPending || !addForm.description || !addForm.allowance_amount}
                  className="bg-indigo-600 text-white text-sm rounded-md px-4 py-1.5 disabled:opacity-50"
                >
                  Add
                </button>
                <button onClick={() => setShowAdd(false)} className="text-slate-500 text-sm px-2">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {allowances.length === 0 && !showAdd && (
        <div className="text-sm text-slate-400 text-center py-8">No allowances set up yet.</div>
      )}

      {allowances.length > 0 && (
        <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Description</th>
                <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Budget</th>
                <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Actual</th>
                <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Delta</th>
                <th className="px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Status</th>
                {isActive && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
              {allowances.map((a) => {
                const delta = Number(a.delta);
                return (
                  <tr key={a.id} className="hover:bg-slate-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{a.description}</td>
                    <td className="px-4 py-2 text-right font-mono text-slate-600 dark:text-slate-400">{fmtNum(a.allowance_amount)}</td>
                    <td className="px-4 py-2 text-right">
                      {isActive && editingId === a.id ? (
                        <div className="flex items-center justify-end gap-1">
                          <input
                            type="number"
                            step="0.01"
                            value={editActual}
                            onChange={e => setEditActual(e.target.value)}
                            className="w-28 border border-indigo-400 rounded px-2 py-0.5 text-xs"
                          />
                          <button
                            onClick={() => updateMutation.mutate({ id: a.id, actual_amount: parseFloat(editActual) })}
                            className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded"
                          >✓</button>
                          <button onClick={() => setEditingId(null)} className="text-xs text-slate-400 px-1">✕</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setEditingId(a.id); setEditActual(a.actual_amount); }}
                          className="font-mono text-slate-600 dark:text-slate-400 hover:underline"
                          disabled={a.status === "reconciled" || !isActive}
                        >
                          {fmtNum(a.actual_amount)}
                        </button>
                      )}
                    </td>
                    <td className={`px-4 py-2 text-right font-mono font-semibold ${
                      delta < 0 ? "text-emerald-700" : delta > 0 ? "text-red-600" : "text-slate-500"
                    }`}>
                      {delta === 0 ? "—" : `${delta < 0 ? "-" : "+"}${fmtNum(Math.abs(delta))}`}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        a.status === "reconciled"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {a.status === "reconciled" ? "✓ Reconciled" : "Open"}
                      </span>
                    </td>
                    {isActive && (
                      <td className="px-4 py-2 text-right">
                        {a.status === "open" && (
                          <button
                            onClick={() => {
                              if (window.confirm("Reconcile this allowance? This will issue a CO if actual ≠ budget.")) {
                                reconcileMutation.mutate(a.id);
                              }
                            }}
                            className="text-xs text-indigo-600 hover:text-indigo-800 border border-indigo-300 rounded px-2 py-0.5"
                          >
                            Reconcile
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Open-book tab ──────────────────────────────────────────────────────────────

function OpenBookTab({ contract }: { contract: GMPContract }) {
  const today = new Date();
  const [period, setPeriod] = useState(isoDate(today));
  const reportQ = useQuery(gmpOpenBookQueryOptions(contract.project_id, period));
  const report = reportQ.data;

  return (
    <div className="space-y-4">
      {/* Period picker */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-700 dark:text-slate-300">
          Period:
          <input
            type="month"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="ml-2 border border-slate-300 rounded-md px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900"
          />
        </label>
        {reportQ.isFetching && <span className="text-xs text-slate-400">Loading…</span>}
      </div>

      {report && (
        <>
          {/* GMP status strip */}
          <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg px-4 py-3 text-sm flex flex-wrap gap-6">
            <div>
              <span className="text-indigo-600 font-medium">Current GMP</span>
              <span className="ml-2 font-mono font-semibold">{fmtNum(report.gmp_status.current_gmp)}</span>
            </div>
            <div>
              <span className="text-indigo-600 font-medium">Contingency</span>
              <span className="ml-2 font-mono">
                {fmtNum(report.gmp_status.contingency_remaining)}
                <span className="text-indigo-400 ml-1 text-xs">
                  ({pct(report.gmp_status.contingency_remaining_pct)})
                </span>
              </span>
            </div>
            <div>
              <span className="text-indigo-600 font-medium">Proj. savings</span>
              <span className="ml-2 font-mono">{fmtNum(report.gmp_status.projected_savings)}</span>
            </div>
            {!report.audit_rights_enabled && (
              <span className="text-amber-600 text-xs border border-amber-300 rounded px-2 py-0.5 bg-amber-50">
                Audit rights off — sub detail redacted
              </span>
            )}
          </div>

          {/* Category table */}
          <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Category</th>
                  <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-slate-500">This Period — Reimb.</th>
                  <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Non-Reimb.</th>
                  <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Total</th>
                  <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-slate-500">To Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                {report.categories.map((cat) => (
                  <tr key={cat.category} className="hover:bg-slate-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-2.5 font-medium text-slate-700 dark:text-slate-300">{cat.label}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{fmtNum(cat.reimbursable)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-500">{fmtNum(cat.non_reimbursable)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900 dark:text-slate-100">{fmtNum(cat.total)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-500 text-xs">
                      {/* To-date is shown from totals not category breakdown; placeholder */}
                      —
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 dark:bg-gray-800 border-t-2 border-slate-300">
                <tr>
                  <td className="px-4 py-2.5 font-semibold text-slate-700 dark:text-slate-200 uppercase text-xs tracking-wide">
                    Total
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-emerald-700">
                    {fmtNum(report.totals.this_period.reimbursable)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-600">
                    {fmtNum(report.totals.this_period.non_reimbursable)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-900 dark:text-slate-100">
                    {fmtNum(report.totals.this_period.total)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-700 dark:text-slate-200">
                    {fmtNum(report.totals.to_date.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {reportQ.error && (
        <div className="text-sm text-red-600">
          Failed to load open-book report: {(reportQ.error as Error).message}
        </div>
      )}
    </div>
  );
}

// ── Closeout tab ───────────────────────────────────────────────────────────────

function CloseoutTab({ contract }: { contract: GMPContract }) {
  const qc = useQueryClient();
  const savingsQ = useQuery(gmpSavingsQueryOptions(contract.id));
  const [gateQ, setGate] = useState<{ ready: boolean; message?: string | null } | null>(null);
  const [loading, setLoading] = useState(false);

  async function checkGate() {
    setLoading(true);
    try {
      const result = await api<{ ready: boolean; message?: string | null; unreconciled_allowances: string[] }>(
        `/api/money/gmp/${contract.id}/closeout-gate`,
      );
      setGate(result);
    } finally {
      setLoading(false);
    }
  }

  const closeMutation = useMutation({
    mutationFn: () =>
      api(`/api/money/gmp/${contract.id}/closeout`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gmpKeys.byProject(contract.project_id) });
      qc.invalidateQueries({ queryKey: gmpKeys.contract(contract.id) });
    },
  });

  const savings = savingsQ.data;
  const isClosed = contract.status === "closed";

  if (isClosed) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-3">✅</div>
        <div className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">Contract Closed</div>
        <div className="text-sm text-slate-500">
          Closed {contract.closed_out_at?.slice(0, 10)} ·{" "}
          Final savings: {fmtNum(contract.final_savings ?? 0)}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Closing out a GMP contract computes the final savings split, transitions
        the contract to Closed, and prepares the credit CO and final AfP for issuance.
      </p>

      {savings && (
        <div className="bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-4 space-y-2 text-sm">
          <div className="font-semibold text-slate-700 dark:text-slate-200 text-xs uppercase tracking-wide mb-2">
            Savings Preview
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Final actual cost</span>
            <span className="font-mono">{fmtNum(savings.final_actual_cost)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Current GMP</span>
            <span className="font-mono">{fmtNum(savings.current_gmp)}</span>
          </div>
          <div className="flex justify-between border-t border-slate-200 dark:border-gray-600 pt-2">
            <span className="font-medium text-slate-700 dark:text-slate-200">Total savings</span>
            <span className={`font-mono font-bold ${savings.savings > 0 ? "text-emerald-700" : "text-slate-500"}`}>
              {fmtNum(savings.savings)}
            </span>
          </div>
          {savings.savings > 0 && (
            <>
              <div className="flex justify-between">
                <span className="text-slate-500">Owner share</span>
                <span className="font-mono">{fmtNum(savings.owner_share)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">GC share</span>
                <span className="font-mono text-emerald-700">{fmtNum(savings.contractor_share)}</span>
              </div>
              {savings.is_capped && (
                <div className="text-xs text-amber-600">⚡ Contractor share capped</div>
              )}
            </>
          )}
        </div>
      )}

      {!gateQ && (
        <button
          onClick={checkGate}
          disabled={loading}
          className="text-sm border border-slate-300 hover:bg-slate-50 rounded-md px-4 py-2 dark:border-gray-700"
        >
          {loading ? "Checking…" : "Check closeout prerequisites"}
        </button>
      )}

      {gateQ && (
        <div className={`text-sm px-4 py-3 rounded-lg border ${
          gateQ.ready
            ? "bg-green-50 border-green-200 text-green-800"
            : "bg-red-50 border-red-200 text-red-700"
        }`}>
          {gateQ.ready
            ? "✓ All prerequisites met — ready to close out."
            : gateQ.message}
        </div>
      )}

      {gateQ?.ready && (
        <button
          onClick={() => {
            if (window.confirm("Close out this GMP contract? This action cannot be undone.")) {
              closeMutation.mutate();
            }
          }}
          disabled={closeMutation.isPending}
          className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md px-5 py-2 disabled:opacity-50"
        >
          {closeMutation.isPending ? "Closing out…" : "Close out contract"}
        </button>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

type Tab = "dashboard" | "contingency" | "allowances" | "open-book" | "closeout";

function GmpPage() {
  const { projectId } = Route.useParams();
  const [activeTab, setActiveTab] = useState<Tab>("dashboard");

  const entitiesQ = useQuery(myEntitiesQueryOptions());
  const currentEntity = entitiesQ.data?.find(e => e.is_current);
  const gmpEnabled = currentEntity?.gmp_enabled ?? false;

  const contractQ = useQuery(gmpContractByProjectQueryOptions(projectId));

  if (!gmpEnabled) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-56px)]">
        <div className="text-center max-w-sm">
          <div className="text-4xl mb-3">🔒</div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            GMP not enabled
          </h2>
          <p className="text-sm text-slate-500">
            GMP contracts are an optional sub-pack. Enable it in Setup → Entity → Capabilities.
          </p>
        </div>
      </div>
    );
  }

  if (contractQ.isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-56px)]">
        <span className="text-sm text-slate-400">Loading…</span>
      </div>
    );
  }

  // 404 = no contract yet → show setup wizard
  if (!contractQ.data) {
    return <ContractSetupForm projectId={projectId} />;
  }

  const contract = contractQ.data;
  const tabs: { key: Tab; label: string }[] = [
    { key: "dashboard", label: "Dashboard" },
    { key: "contingency", label: "Contingency" },
    { key: "allowances", label: "Allowances" },
    { key: "open-book", label: "Open Book" },
    { key: "closeout", label: "Closeout" },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            GMP Contract
          </h1>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            contract.status === "active" ? "bg-green-100 text-green-700" :
            contract.status === "closing_out" ? "bg-amber-100 text-amber-700" :
            "bg-slate-100 text-slate-600"
          }`}>
            {contract.status === "active" ? "Active" : contract.status === "closing_out" ? "Closing Out" : "Closed"}
          </span>
          <div className="flex-1" />
          <span className="text-xs text-slate-400 font-mono">
            Current GMP: {fmtNum(contract.current_gmp)}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-6 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex gap-0">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${
                activeTab === key
                  ? "border-indigo-600 text-indigo-700 dark:text-indigo-400 font-medium"
                  : "border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto px-6 py-6">
        {activeTab === "dashboard" && <DashboardTab contract={contract} />}
        {activeTab === "contingency" && <ContingencyTab contract={contract} />}
        {activeTab === "allowances" && <AllowancesTab contract={contract} />}
        {activeTab === "open-book" && <OpenBookTab contract={contract} />}
        {activeTab === "closeout" && <CloseoutTab contract={contract} />}
      </div>
    </div>
  );
}
